import {useCallback} from 'react';

import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from '@newsela/snitch';
import {useSelector} from 'react-redux';
import {v4 as uuidv4} from 'uuid';

import {NAVBAR_BREAKPOINTS, SEARCH_SESSION_KEY} from './constants';
import {getUserProperties} from '../../../../../modules/selectors/userSelectors';

export const MIN_LICENSE_WIDTH = 90;

export const useGetProductsVisibilityBreakpoint = () => {
  const {
    isStaff,
    hasFeature,
    activeELALicense,
    activeSSLicense,
    activeScienceLicense,
    activeEssentialsLicense,
  } = useSelector((state) => getUserProperties(state));
  const canViewAdminConsole = hasFeature('can_view_admin_console');

  const canSeeAdminMenu = canViewAdminConsole || isStaff;
  const hiddenMenus = [
    activeELALicense,
    activeEssentialsLicense,
    activeScienceLicense,
    activeSSLicense,
    canSeeAdminMenu
  ].filter((menu) => !menu).length;
  return NAVBAR_BREAKPOINTS.lg - (hiddenMenus * MIN_LICENSE_WIDTH);
};

/**
 * A custom hook for initializing a new Snitch search tracking session.
 *
 * When the returned function `startSearchTracker` is called, this
 * hook performs four main actions:
 * 1. Generates a new unique ID for the search session.
 * 2. Persists the ID to `window.sessionStorage`.
 * 3. Updates Snitch's search session ID.
 * 4. Fires a Snitch event indicating a search has been initiated.
 *
 * @param metadata - Optional metadata attached to the Snitch event.
 * @returns Object containing the `startSearchTracker` function.
 */
export function useNewSearchSessionTracker(metadata) {
  const {trackAction, getEventName, setSearchSessionId} = useTracker();
  const startSearchTracker = useCallback(
    (searchInput) => {
      const newSearchSessionId = uuidv4();
      window.sessionStorage.setItem(SEARCH_SESSION_KEY, newSearchSessionId);
      setSearchSessionId(newSearchSessionId);

      trackAction({
        event_name: getEventName(
          objectTypes.content,
          actionPrefixes.initiate,
          actionNames.search,
        ),
        event_type: eventTypes.nativeInteraction,
        dimension_properties: {
          description: 'Search Session Started',
          search_session_id: newSearchSessionId,
          search_term: searchInput,
          ...(metadata ? {metadata} : null),
        },
      });
    },
    [trackAction, getEventName, setSearchSessionId],
  );
  return {startSearchTracker};
}
